
import {cookies, CookKey} from 'utils/cookies';

import {normalizePath, truncate} from './util';

// Ad-platform click ids, in priority order when several are present
const CLICK_IDS = [
	'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid', 'twclid', 'irclickid',
] as const;

const UTM_KEYS = [
	'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
] as const;

export type Touch = {
	utm_source?: string
	utm_medium?: string
	utm_campaign?: string
	utm_term?: string
	utm_content?: string
	click_id?: string
	click_id_type?: string
	referrer?: string
	landing: string
	ts: number
};

// Only off-site referrers count as a traffic source
const externalReferrer = (): string | undefined => {
	if (!document.referrer) {
		return undefined;
	}
	try {
		const ref = new URL(document.referrer);
		if (ref.hostname === window.location.hostname) {
			return undefined;
		}
		return truncate(`${ref.protocol}//${ref.hostname}${normalizePath(ref.pathname)}`, 2048);
	} catch {
		return undefined;
	}
};

export const readTouch = (): Touch => {
	const params = new URLSearchParams(window.location.search);
	const touch: Touch = {
		landing: normalizePath(window.location.pathname),
		referrer: externalReferrer(),
		ts: Date.now(),
	};
	for (const key of UTM_KEYS) {
		const value = params.get(key);
		if (value) {
			touch[key] = truncate(value, 256);
		}
	}
	for (const key of CLICK_IDS) {
		const value = params.get(key);
		if (value) {
			touch.click_id = truncate(value, 256);
			touch.click_id_type = key;
			break;
		}
	}
	return touch;
};

// A changed campaign mid-visit starts a new session (GA-style)
export const campaignKey = (t: Touch): string => [
	t.utm_source, t.utm_medium, t.utm_campaign, t.click_id,
].map((v) => v || '').join('|');

export const hasCampaign = (t: Touch): boolean => campaignKey(t) !== '|||';

// First touch ever seen in this browser; kept for a year
export const firstTouch = (current: Touch): Touch => {
	const stored = cookies.get(CookKey.ObserverFirstTouch);
	if (stored) {
		try {
			return JSON.parse(stored) as Touch;
		} catch {
			// fall through and overwrite a corrupt cookie
		}
	}
	cookies.set(CookKey.ObserverFirstTouch, JSON.stringify(current), {expires: 365});
	return current;
};

export const referralCode = (): string | undefined =>
	truncate(cookies.get(CookKey.Referrer), 128);
